// ─────────────────────────────────────────────────────────────────────────────
// GOOGLE ADS LOADS ON SIX PAGES. EVERYWHERE ELSE IT IS NOT IN THE DOCUMENT.
//
// 🚨 IT USED TO BE IN index.html, which in a single-page app means EVERY route:
// the borrower's dashboard, their documents, DoorlyPro, DoorlyDesk. Google
// received a page_location for all of them. Those URLs are not anonymous — the
// authenticated surfaces are where the loan lives — and the tag was there
// because index.html is the only place anybody thinks to put a script tag, not
// because anything on those pages needed it.
//
// 🔑 DEFAULT-DENY, WITH THE ALLOWED PAGES ENUMERATED. An exclusion list is the
// wrong shape: the next authenticated route somebody adds would be tracked by
// omission. These six are the public marketing funnel, and a route that is not
// written here does not load the tag no matter what it is.
//
// ⚠️ AND BEING ALLOW-LISTED IS NOT SUFFICIENT ON ITS OWN. The path is checked
// AND the full URL is checked for an identifier, because an allow-listed page
// reached as `/thank-you?loan=4412` would hand Google the loan id in
// page_location. Two gates, and both have to say yes.
//
// 🚨 WHAT IS DELIBERATELY NOT HERE: /underwriting, /price-tool, /product-demo
// and /forecast. /underwriting, /price-tool and /forecast are confidential pages
// whose content is served only to signed-in, authorized sessions (2026-10-21 —
// no shared passwords any more); /product-demo is public but internal-facing.
// None is a marketing page, so none is tagged.
// ─────────────────────────────────────────────────────────────────────────────

export const GOOGLE_ADS_ID = 'AW-18194838080';

// ⚠️ EXACT PATHS, NOT PREFIXES. '/market' as a prefix would match
// /market/portal/documents — an authenticated investor document room.
export const MARKETING_ROUTES = Object.freeze([
  '/',                     // LandingPage
  '/pre-qualify',          // PreQualification — the public funnel entry
  '/thank-you',            // ThankYou
  '/market',               // MarketLanding
  '/market/sample-tape',   // SampleTape
  '/doorlypro/start',      // DoorlyProStart — the partner funnel entry
]);

// 🔑 THE CONVERSIONS, NAMED, so what is sent is readable in one place rather
// than inferred from a send_to string at the call site.
export const CONVERSIONS = Object.freeze({
  // ⚠️ MOVED, NOT INVENTED. This fired on the borrower DASHBOARD — an
  // authenticated route, and therefore the one place it could not stay. It now
  // fires at the end of /pre-qualify, the last public step before /register.
  // That counts an EARLIER and WIDER event than "reached the dashboard": see
  // docs/follow-ups.md. It sends send_to and nothing else.
  borrower_application: `${GOOGLE_ADS_ID}/JNmICP6shrUcEMDk_OND`,
});

const normalisePath = (p) => {
  const s = String(p || '/');
  return s.length > 1 && s.endsWith('/') ? s.slice(0, -1) : s;
};

// ⚠️ A QUERY KEY THAT NAMES A SUBJECT OR A RECORD. Matched on whole
// underscore/hyphen-delimited words so `grid` does not match `id`.
const ID_KEY_RE = /(^|[_-])(id|uuid|guid|token|key|loan|application|app|borrower|coborrower|submission|referral|user|account|partner|email|inquiry|lead|file)([_-]|$)/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OPAQUE_RE = /^[A-Za-z0-9_-]{20,}$/;

/**
 * 🚨 DOES THIS URL NAME SOMEBODY OR SOMETHING. Conservative on purpose: a
 * false positive costs one page of ad attribution, a false negative sends a
 * loan identifier to Google.
 */
export function carriesIdentifier(pathname, search) {
  for (const seg of String(pathname || '').split('/')) {
    if (!seg) continue;
    // ⚠️ A BARE NUMBER IS AN ID. /homes/4412, /referrals/19.
    if (/^\d+$/.test(seg)) return true;
    if (UUID_RE.test(seg)) return true;
    if (OPAQUE_RE.test(seg) && /\d/.test(seg)) return true;
  }
  let params;
  try {
    params = new URLSearchParams(String(search || ''));
  } catch (_) {
    // 🔑 UNPARSEABLE IS NOT CLEAN. If the query cannot be read it cannot be
    // cleared, so it is treated as carrying something.
    return true;
  }
  for (const [k, v] of params.entries()) {
    if (ID_KEY_RE.test(k)) return true;
    const val = String(v || '');
    if (val.includes('@')) return true;          // an email address
    if (UUID_RE.test(val)) return true;
    if (OPAQUE_RE.test(val) && /\d/.test(val)) return true;
  }
  return false;
}

/** 🔑 BOTH GATES. Allow-listed AND anonymous. */
export function mayLoadTag(pathname, search) {
  if (!MARKETING_ROUTES.includes(normalisePath(pathname))) return false;
  return !carriesIdentifier(pathname, search);
}

const SCRIPT_ID = 'google-ads-tag';
// 🚨 GOOGLE'S OWN KILL SWITCH. Removing the <script> element does not unload
// the library that already ran — the window globals and any timers it set
// survive. `ga-disable-<ID>` is the documented flag that makes gtag.js refuse
// to send, so teardown does not depend on evicting code from the page.
const DISABLE_FLAG = `ga-disable-${GOOGLE_ADS_ID}`;

const THIRD_PARTY_SRC = /googletagmanager\.com|google-analytics\.com|googleadservices\.com|doubleclick\.net/;

function removeInjected() {
  if (typeof document === 'undefined') return;
  for (const el of [...document.querySelectorAll('script[src]')]) {
    if (THIRD_PARTY_SRC.test(el.getAttribute('src') || '')) el.remove();
  }
  for (const el of [...document.querySelectorAll('iframe[src]')]) {
    if (THIRD_PARTY_SRC.test(el.getAttribute('src') || '')) el.remove();
  }
}

/** Inject the tag. Idempotent. */
export function loadGoogleAds() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  window[DISABLE_FLAG] = false;
  if (document.getElementById(SCRIPT_ID)) return;
  window.dataLayer = window.dataLayer || [];
  // ⚠️ THE ARGUMENTS OBJECT, NOT A SPREAD. gtag.js reads `arguments` off what
  // is pushed; pushing an array of the same values is not the same thing.
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GOOGLE_ADS_ID);
  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);
}

/**
 * 🚨 LEAVING A MARKETING PAGE TEARS IT DOWN. A SPA does not reload, so without
 * this a borrower who lands on `/` and signs in carries the tag into their
 * dashboard for the rest of the session — which is the exact leak this file
 * exists to close, surviving the fix.
 */
export function unloadGoogleAds() {
  if (typeof window === 'undefined') return;
  window[DISABLE_FLAG] = true;
  removeInjected();
  try { delete window.gtag; } catch (_) { window.gtag = undefined; }
  try { delete window.dataLayer; } catch (_) { window.dataLayer = undefined; }
}

/** Called on every navigation. One decision, both directions. */
export function syncMarketingTag(pathname, search) {
  if (mayLoadTag(pathname, search)) loadGoogleAds();
  else unloadGoogleAds();
}

/**
 * Fire a named conversion.
 *
 * 🚨 send_to AND NOTHING ELSE. No value, no transaction id, no user data, no
 * application field. Enhanced conversions would hash an email into this call,
 * and there is nowhere in this signature to put one.
 *
 * ⚠️ IT DOES NOT LOAD THE TAG. A conversion fired where the tag is not allowed
 * is a conversion on a page that should not be talking to Google, so it is
 * dropped rather than becoming a reason to inject.
 */
export function fireConversion(name) {
  const sendTo = CONVERSIONS[name];
  if (!sendTo) return false;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return false;
  if (window[DISABLE_FLAG]) return false;
  window.gtag('event', 'conversion', { send_to: sendTo });
  return true;
}

export const __test = { SCRIPT_ID, DISABLE_FLAG, normalisePath };
